import React, { FunctionComponent, useState } from "react";
import { connect, MapDispatchToProps, MapStateToProps } from "react-redux";

import { createChangeScaleIndexAction, createRequestPaintingScrollPositionAction } from "@multiweb/picasso";
import { ButtonBackgroundColor, IconButton, MessageWindow } from "@multiweb/reactComponents";
import { getPrimaryText } from "@multiweb/reactCore/i18n";
import { IconCode } from "@multiweb/types/contracts";
import { IAppState } from "@multiweb/types/react";
import { createSetTouchedAction } from "../../../../workflowEditor/actions";
import { LOC_CANCEL, LOC_RELOAD, LOC_RELOAD_WORKFLOW_DIALOG, LOC_SAVE, LOC_UPDATES_THE_CONTENT } from "../../../../workflowEditor/locKeys";
import { isTouched } from "../../../../workflowEditor/selectors";
import { MessageWindowState, WorkflowEditorProps } from "../../../../workflowEditor/types";
import { ToolbarSection } from "../components";

export type ObjectHandlingSectionComponentProps = {
    readonly touched: boolean;
    readonly onReloadClick: () => void;
    readonly changeScaleIndex: (newScaleIndex: number) => void;
    readonly resetPaintingOffset: () => void;
    readonly setTouched: (touched: boolean) => void;
    readonly saveWorkflow: () => Promise<boolean>;
    readonly reloadWorkflowDialogText: string;
    readonly updatesTheContentText: string;
    readonly saveText: string;
};

const ObjectHandlingSectionComponent: FunctionComponent<ObjectHandlingSectionComponentProps> = ({
    touched,
    onReloadClick,
    changeScaleIndex,
    saveWorkflow,
    resetPaintingOffset,
    setTouched,
    reloadWorkflowDialogText,
    updatesTheContentText,
    saveText,
}) => {
    const [messageWindowState, setMessageWindowState] = useState<MessageWindowState>({ isOpen: false, properties: null });

    const onCloseMessageWindow = () => {
        setMessageWindowState({ isOpen: false, properties: null });
    };

    const handleReload = () => {
        onReloadClick();
        resetPaintingOffset();
        changeScaleIndex(1);
    };

    const handleReloadButtonClick = () => {
        if (touched)
            setMessageWindowState({
                isOpen: true,
                properties: {
                    children: reloadWorkflowDialogText,
                    primaryButtonLocKey: LOC_RELOAD,
                    secondaryButtonLocKey: LOC_CANCEL,
                    onPrimaryButtonClick: handleReload,
                    onCloseMessageWindow,
                },
            });
        else
            handleReload();
    };

    const handleSaveWorkflow = async () => {
        const successful = await saveWorkflow();
        if (touched && successful)
            setTouched(false);
    };

    return (
        <ToolbarSection>
            <IconButton iconCode={IconCode.uiRefresh} outline={false} onClick={handleReloadButtonClick} title={updatesTheContentText} backgroundColor={ButtonBackgroundColor.transparent} />
            <IconButton iconCode={IconCode.save} disabled={!touched} outline={false} onClick={handleSaveWorkflow} title={saveText} backgroundColor={ButtonBackgroundColor.transparent} />
            {(messageWindowState.isOpen && messageWindowState.properties !== null) && <MessageWindow {...messageWindowState.properties} />}
        </ToolbarSection>
    );
};

type StateProps = Pick<ObjectHandlingSectionComponentProps, "touched" | "reloadWorkflowDialogText" | "updatesTheContentText" | "saveText">;

const mapStateToProps: MapStateToProps<StateProps, WorkflowEditorProps, IAppState> = (state, { hostKey }) => ({
    touched: isTouched(state, hostKey),
    reloadWorkflowDialogText: getPrimaryText(state, LOC_RELOAD_WORKFLOW_DIALOG) ?? "Opravdu chcete přenačíst instanci workflow? Přijdete o všechny neuložené změny.",
    updatesTheContentText: getPrimaryText(state, LOC_UPDATES_THE_CONTENT) ?? "Aktualizuje obsah",
    saveText: getPrimaryText(state, LOC_SAVE) ?? "Uložit",
});

type DispatchProps = Pick<ObjectHandlingSectionComponentProps, "changeScaleIndex" | "resetPaintingOffset" | "setTouched">;

const mapDispatchToProps: MapDispatchToProps<DispatchProps, WorkflowEditorProps> = (dispatch, { hostKey }) => ({
    changeScaleIndex: (newScaleIndex) => dispatch(createChangeScaleIndexAction(hostKey, newScaleIndex)),
    resetPaintingOffset: () => dispatch(createRequestPaintingScrollPositionAction(hostKey, { left: 0, top: 0 })),
    setTouched: (touched) => dispatch(createSetTouchedAction(hostKey, touched)),
});

export const ObjectHandlingSection = connect(mapStateToProps, mapDispatchToProps)(ObjectHandlingSectionComponent);
